import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
// Use the shared api instance so the CSRF interceptor adds X-CSRF-Token (absolute URLs below override baseURL).
import axios from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles, CheckCircle2, XCircle, AlertTriangle, Clock,
  ArrowLeft, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MCPDraftReview() {
  const { draftId } = useParams();
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/mcp/drafts/${draftId}/review`,
                                  { withCredentials: true });
      setDraft(r.data);
    } catch (e) {
      const code = e?.response?.data?.detail?.code || "DRAFT_NOT_FOUND";
      setError(code === "DRAFT_NOT_FOUND"
                ? "This AI-created draft was not found or does not belong to you."
                : e?.response?.data?.detail?.message || "Failed to load draft.");
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/mcp/drafts/${draftId}/accept`,
        { expected_version: draft.version },
        { withCredentials: true });
      const cid = r.data?.campaign_id;
      if (r.data?.note === "already_accepted") {
        toast.info("This draft was already accepted.");
      } else {
        toast.success("Campaign created. Awaiting admin approval.");
      }
      nav(cid ? `/merchant/campaigns` : "/merchant/coupons");
    } catch (e) {
      const d = e?.response?.data?.detail || {};
      if (d.code === "STALE_REVIEW") {
        toast.error("This draft has changed since you opened it. Reloading…");
        await load();
      } else if (d.code === "DRAFT_INVALID_ON_ACCEPT") {
        toast.error("Draft is no longer valid — see errors below.");
        setDraft(prev => prev ? { ...prev, errors: d.errors,
                                     validation_status: "INVALID" } : prev);
      } else if (d.code === "DRAFT_EXPIRED") {
        toast.error(d.message);
        setDraft(prev => prev ? { ...prev, status: "expired" } : prev);
      } else {
        toast.error(d.message || "Could not accept the draft.");
      }
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/mcp/drafts/${draftId}/reject`,
                        { reason: "merchant_declined" },
                        { withCredentials: true });
      toast.success("Draft discarded.");
      nav("/merchant/coupons");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not discard.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-cyan-600"
                  data-testid="mcp-review-loading" />
      </div>
    );
  }

  if (error || !draft) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4"
            data-testid="mcp-review-error">
        <div className="pf-card p-8 text-center">
          <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">Draft not available</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <Link to="/merchant/coupons"
                 className="pf-btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to coupons
          </Link>
        </div>
      </div>
    );
  }

  const f = draft.fields || {};
  const canAccept = draft.status === "draft"
                     && draft.validation_status === "VALID";

  const StatusPill = () => {
    if (draft.status === "accepted")
      return <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs">Accepted</span>;
    if (draft.status === "rejected")
      return <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-xs">Discarded</span>;
    if (draft.status === "expired")
      return <span className="px-2 py-0.5 rounded bg-slate-200 text-slate-700 text-xs">Expired</span>;
    if (draft.validation_status === "VALID")
      return <span className="px-2 py-0.5 rounded bg-cyan-100 text-cyan-800 text-xs">Ready to accept</span>;
    if (draft.validation_status === "INCOMPLETE")
      return <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs">Incomplete</span>;
    return <span className="px-2 py-0.5 rounded bg-rose-100 text-rose-800 text-xs">Invalid</span>;
  };

  return (
    <div className="max-w-3xl mx-auto py-10 px-4" data-testid="mcp-review-root">
      <div className="mb-6 flex items-center gap-3">
        <Sparkles className="w-6 h-6 text-cyan-600" />
        <div>
          <h1 className="text-2xl font-semibold">Review AI draft</h1>
          <p className="text-sm text-slate-600">
            Created through: External AI (MCP) · client{" "}
            <code className="text-xs">{draft.client_id}</code>
          </p>
        </div>
        <div className="ml-auto"><StatusPill /></div>
      </div>

      {draft.status === "expired" && (
        <div className="pf-card p-4 mb-5 border-l-4 border-slate-400"
              data-testid="mcp-review-expired">
          <div className="flex gap-2 items-start">
            <Clock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm">
              This AI-created campaign draft has expired. Create a new draft
              from your connected AI assistant.
            </p>
          </div>
        </div>
      )}

      {(draft.validation_status === "INCOMPLETE"
        || draft.validation_status === "INVALID") && (
        <div className="pf-card p-4 mb-5 border-l-4 border-amber-500"
              data-testid="mcp-review-errors">
          <div className="flex gap-2 items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium mb-2">
                This draft is not ready to accept:
              </p>
              <ul className="text-sm space-y-1">
                {(draft.errors || []).map((e, i) => (
                  <li key={i} className="text-slate-700">
                    <code className="text-xs text-amber-700">{e.code}</code>
                    {" · "}{e.field || "—"} — {e.message}
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-500 mt-3">
                Ask your AI assistant to update the draft, then reload this
                page.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="pf-card p-6 space-y-4" data-testid="mcp-review-summary">
        <Row label="Name">{f.name || "—"}</Row>
        <Row label="Description">{f.description || "—"}</Row>
        <Row label="Channel">{f.campaign_type} · {f.mode}</Row>
        <Row label="Discount">
          {f.discount_type} · {f.discount_value}
          {f.discount_type === "percent" ? "%" : ""}
        </Row>
        <Row label="Runs">
          {(f.starts_at || "").slice(0, 10)} → {(f.expires_at || "").slice(0, 10)}
        </Row>
        <Row label="Regions">
          {(f.countries || []).join(", ") || "—"}
          {f.provinces?.length ? ` · ${(f.provinces || []).join(", ")}` : ""}
        </Row>
        <Row label="Products">
          {(f.product_ids || []).length
            ? `${f.product_ids.length} product(s)` : "All products"}
        </Row>
        {f.min_purchase ? <Row label="Min purchase">CA${f.min_purchase}</Row> : null}
        {f.terms_conditions
          ? <Row label="Terms">{f.terms_conditions}</Row>
          : null}
        <Row label="Draft version">v{draft.version}</Row>
        <Row label="Expires">
          {(draft.expires_at || "").replace("T", " ").slice(0, 16)} UTC
        </Row>
      </div>

      <div className="mt-6 flex gap-3 flex-wrap">
        <button onClick={reject} disabled={busy || draft.status !== "draft"}
                 data-testid="mcp-review-reject"
                 className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 text-sm font-medium disabled:opacity-40">
          Discard
        </button>
        <Link to="/merchant/coupons" data-testid="mcp-review-back"
               className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium border border-slate-200">
          Return
        </Link>
        <button onClick={accept} disabled={busy || !canAccept}
                 data-testid="mcp-review-accept"
                 className="ml-auto pf-btn-primary inline-flex items-center gap-2 disabled:opacity-40">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          <CheckCircle2 className="w-4 h-4" />
          Accept & create campaign
        </button>
      </div>
      <p className="text-xs text-slate-500 mt-4">
        Accepting creates a Cyannect campaign in pending status via the
        existing merchant campaign workflow. External AI never publishes or
        activates campaigns — that remains under your control.
      </p>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-4 text-sm">
      <div className="text-slate-500">{label}</div>
      <div className="col-span-2 text-slate-900">{children}</div>
    </div>
  );
}
